import React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import {
    displayChipTypes,
    normalizeChipType,
    sortedDisplayChips
} from '../fields/displayMetadata';
import { getLifecycleStatus, lifecycleStatuses } from '../fields/lifecycleMetadata';
import CatalogEditorDialog from './CatalogEditorDialog';
import EmailShellEditorDialogs from './EmailShellEditorDialogs';
import HomePhoneEditorDialog from './HomePhoneEditorDialog';
import InternetEditorDialog from './InternetEditorDialog';
import TvDisplayEditorDialog from './TvDisplayEditorDialog';

function ProductPricingEditorDialogs({ context }) {
    const {
        chipColor,
        formatMoney,
        linesFromText
    } = context;

    const renderOfferLifecycleControls = ({ values, updateValue, canDelete, onDelete, helperScope }) => (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Lifecycle</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Archive removes published products from Bundle Builder while keeping history and rollback safe. Delete is only available for draft-only products.
                    </Typography>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Status"
                            select
                            value={values.status}
                            onChange={(event) => updateValue('status', event.target.value)}
                            fullWidth
                            SelectProps={{ native: true }}
                        >
                            {lifecycleStatuses.map((status) => (
                                <option key={status.value} value={status.value}>{status.label}</option>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Start date/time"
                            type="datetime-local"
                            value={values.startsAt}
                            onChange={(event) => updateValue('startsAt', event.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Expiry date/time"
                            type="datetime-local"
                            value={values.expiresAt}
                            onChange={(event) => updateValue('expiresAt', event.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                    <FormControlLabel
                        control={<Switch checked={values.visibleInAdminBuilder} onChange={(event) => updateValue('visibleInAdminBuilder', event.target.checked)} />}
                        label="Visible in admin builder"
                    />
                    <FormControlLabel
                        control={<Switch checked={values.visibleInAgentBuilder} onChange={(event) => updateValue('visibleInAgentBuilder', event.target.checked)} />}
                        label="Visible in agent builder"
                    />
                    <FormControlLabel
                        control={<Switch checked={values.autoHideAfterExpiry} onChange={(event) => updateValue('autoHideAfterExpiry', event.target.checked)} />}
                        label="Auto-hide after expiry"
                    />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => {
                            updateValue('status', 'archived');
                            updateValue('visible', false);
                            updateValue('visibleInAdminBuilder', false);
                            updateValue('visibleInAgentBuilder', false);
                            updateValue('archivedAt', new Date().toISOString());
                        }}
                    >
                        Archive Product
                    </Button>
                    <Button variant="outlined" color="error" disabled={!canDelete} onClick={onDelete}>
                        Delete Draft-Only Product
                    </Button>
                </Stack>
                {!canDelete && (
                    <Typography variant="caption" color="text.secondary">
                        Published {helperScope} products cannot be deleted. Archive or hide them instead.
                    </Typography>
                )}
            </Stack>
        </Paper>
    );

    const renderChipListEditor = ({
        title,
        helperText,
        chips = [],
        onUpdate,
        onAdd,
        onRemove,
        addLabel = 'Add chip'
    }) => (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
                        {helperText && (
                            <Typography variant="body2" color="text.secondary">
                                {helperText}
                            </Typography>
                        )}
                    </Box>
                    <Button variant="outlined" size="small" onClick={onAdd}>
                        {addLabel}
                    </Button>
                </Stack>
                {sortedDisplayChips(chips).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No items configured. The card will simply show no {title.toLowerCase()}.
                    </Typography>
                ) : (
                    sortedDisplayChips(chips).map((chip) => {
                        const chipIndex = chips.indexOf(chip);

                        return (
                            <Grid container spacing={1.5} key={`${chip.text}-${chipIndex}`} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Text"
                                        value={chip.text}
                                        onChange={(event) => onUpdate(chipIndex, 'text', event.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={6} md={2}>
                                    <TextField
                                        label="Order"
                                        type="number"
                                        value={chip.order}
                                        onChange={(event) => onUpdate(chipIndex, 'order', event.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={6} md={2}>
                                    <TextField
                                        label="Type"
                                        select
                                        value={normalizeChipType(chip.type)}
                                        onChange={(event) => onUpdate(chipIndex, 'type', event.target.value)}
                                        fullWidth
                                        size="small"
                                        SelectProps={{ native: true }}
                                    >
                                        {displayChipTypes.map((type) => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={6} md={2}>
                                    <FormControlLabel
                                        control={<Switch checked={chip.visible} onChange={(event) => onUpdate(chipIndex, 'visible', event.target.checked)} />}
                                        label={chip.visible ? 'Show' : 'Hidden'}
                                    />
                                </Grid>
                                <Grid item xs={6} md={2}>
                                    <Button variant="text" color="error" size="small" onClick={() => onRemove(chipIndex)}>
                                        Delete
                                    </Button>
                                </Grid>
                            </Grid>
                        );
                    })
                )}
            </Stack>
        </Paper>
    );

    const renderProductDraftPreview = (values, options = {}) => {
        const topBadges = sortedDisplayChips(values.topBadges || []).filter((chip) => chip.visible && chip.text);
        const detailChips = sortedDisplayChips(values.displayChips || []).filter((chip) => chip.visible && chip.text);
        const status = getLifecycleStatus(values);
        const agentHidden = values.visible === false || values.visibleInAgentBuilder === false || ['scheduled', 'expired', 'archived'].includes(status);
        const price = options.priceValue !== undefined ? options.priceValue : values.price;
        const emailBullets = linesFromText(values.emailBulletsText);

        return (
            <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', height: '100%' }}>
                        <Stack spacing={1.25}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={700}>Agent-facing Bundle Builder card</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Draft preview. Agents will see this only after publish.
                                </Typography>
                            </Box>
                            {agentHidden && (
                                <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                                    This product will not appear to agents.
                                </Alert>
                            )}
                            <Card
                                variant="outlined"
                                sx={{
                                    bgcolor: 'background.paper',
                                    borderColor: agentHidden ? 'warning.light' : 'divider',
                                    opacity: agentHidden ? 0.72 : 1
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Stack spacing={1.25}>
                                        {topBadges.length > 0 && (
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {topBadges.map((chip, index) => (
                                                    <Chip
                                                        key={`${chip.text}-${index}`}
                                                        label={chip.text || 'Untitled badge'}
                                                        size="small"
                                                        color={chipColor(chip.type)}
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                        <Box display="flex" justifyContent="space-between" gap={2}>
                                            <Box>
                                                <Typography variant="subtitle1">
                                                    {values.displayName || `${options.categoryLabel || 'Product'} product`}
                                                </Typography>
                                                {values.cardDescription && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {values.cardDescription}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {price !== undefined && price !== null && (
                                                <Typography variant="h6" color="primary.main" sx={{ whiteSpace: 'nowrap' }}>
                                                    {formatMoney(price)}
                                                </Typography>
                                            )}
                                        </Box>
                                        {detailChips.length > 0 && (
                                            <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                                                {detailChips.map((chip, index) => (
                                                    <Chip
                                                        key={`${chip.text}-${index}`}
                                                        label={chip.text || 'Untitled chip'}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                        {values.displayHighlightNote && (
                                            <Typography variant="caption" color="text.secondary">
                                                {values.displayHighlightNote}
                                            </Typography>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Admin status, not shown to agents</Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                    {[
                                        status.charAt(0).toUpperCase() + status.slice(1),
                                        values.visible === false ? 'Hidden' : 'Visible',
                                        values.visibleInAgentBuilder === false ? 'Agent hidden' : 'Agent-visible',
                                        values.quoteEligible === false ? 'Not quote eligible' : 'Quote eligible',
                                        values.customerEmailEligible ? 'Customer email eligible' : 'Email excluded',
                                        values.separateFlowOnly ? 'Separate flow only' : null
                                    ].filter(Boolean).map((label) => (
                                        <Chip
                                            key={label}
                                            label={label}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Stack spacing={1}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={700}>Customer email row preview</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Separate from the agent card. This controls customer-facing email output only.
                                </Typography>
                            </Box>
                            <Chip
                                label={values.customerEmailEligible ? 'Included in customer email' : 'Excluded from customer email'}
                                size="small"
                                color={values.customerEmailEligible ? 'success' : 'default'}
                                variant="outlined"
                                sx={{ width: 'fit-content' }}
                            />
                            <Typography variant="body2" fontWeight={700}>
                                {values.emailDisplayName || values.displayName || 'Customer-facing product name'}
                            </Typography>
                            {emailBullets.length > 0 ? (
                                <List dense disablePadding>
                                    {emailBullets.map((bullet) => (
                                        <ListItem key={bullet} disableGutters sx={{ py: 0 }}>
                                            <ListItemText primary={`• ${bullet}`} primaryTypographyProps={{ variant: 'body2' }} />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No customer-facing bullets configured.
                                </Typography>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            <InternetEditorDialog context={context} primitives={{ renderChipListEditor, renderOfferLifecycleControls, renderProductDraftPreview }} />
            <HomePhoneEditorDialog context={context} primitives={{ renderChipListEditor, renderOfferLifecycleControls, renderProductDraftPreview }} />
            <CatalogEditorDialog context={context} primitives={{ renderChipListEditor, renderOfferLifecycleControls, renderProductDraftPreview }} />
            <TvDisplayEditorDialog context={context} primitives={{ renderChipListEditor, renderProductDraftPreview }} />
            <EmailShellEditorDialogs context={context} primitives={{}} />
        </>
    );
}

export default ProductPricingEditorDialogs;
